import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminUserService } from '@/shared/api'
import type { UserListRequest, UpdateUserStatusRequest, AdminUserCreateRequest } from '@/shared/api'

// Ключи для React Query
const QUERY_KEYS = {
  users: (params?: UserListRequest) => ['users', params],
  userDetails: (userId: number) => ['users', userId],
}

export function useUsers(params?: UserListRequest) {
  return useQuery({
    queryKey: QUERY_KEYS.users(params),
    queryFn: () => AdminUserService.getUsers(params),
    staleTime: 5 * 60 * 1000, // 5 минут
  })
}

export function useUserDetails(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.userDetails(userId),
    queryFn: () => AdminUserService.getUserDetails(userId),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false && userId > 0,
  })
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, request }: { userId: number; request: UpdateUserStatusRequest }) =>
      AdminUserService.updateUserStatus(userId, request),
    onSuccess: (_, { userId }) => {
      toast.success('Статус пользователя обновлен')
      
      // Инвалидируем кеш
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userDetails(userId) })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка обновления статуса')
      }
    },
  })
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AdminUserCreateRequest) => AdminUserService.createUser(request),
    onSuccess: () => {
      toast.success('Сотрудник создан')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка создания сотрудника')
      }
    },
  })
}
